import { useLayoutEffect } from "react";
import {
    BOARD_CARD_ATTRIBUTE,
    PLACEMENT_KEY_ATTRIBUTE,
} from "./boardAttributes";
import type { BoardCells } from "./boardCells";

// A remounted card shows within a few frames; past this it isn't coming back.
export const RESTORE_TIMEOUT_MS = 500;

/** Focus fell to the page or the board root, as when its card unmounted. */
const isFocusLost = (root: HTMLElement) => {
    const active = document.activeElement;
    return active === null || active === document.body || active === root;
};

/** The card inside the `li` with `placementKey`, if it's mounted. */
const findCard = (root: HTMLElement, placementKey: string) =>
    Array.from(root.querySelectorAll(`[${PLACEMENT_KEY_ATTRIBUTE}]`))
        .find(
            (slot) =>
                slot.getAttribute(PLACEMENT_KEY_ATTRIBUTE) === placementKey,
        )
        ?.querySelector<HTMLElement>(`[${BOARD_CARD_ATTRIBUTE}]`) ?? null;

interface UseBoardFocusRestoreArgs<TItem> {
    root: HTMLElement | null;
    /**
     * From `useBoardFocusedKey`: still set after the card unmounted, set
     * while focus is in a popup the card opened, and moved to the item's
     * new card when an edit moves it.
     */
    focusedKey: string | null;
    cells: BoardCells<TItem>;
    /** Keys of the windowed cells, sorted. */
    windowedCellKeys: readonly string[];
    /** Clears `focusedKey`. Stable, so it doesn't restart the wait. */
    onGiveUp: () => void;
}

/**
 * Remounting a card drops focus to the page: a list switching between plain
 * and windowed, an edit that moves the card to another cell, or one that
 * unmounts the control or popup focus was in. This puts focus back on the
 * focused card once it mounts (a windowed list mounts its cards a commit
 * later); while focus is still in the card's popup, it waits for that to
 * close. It stops waiting once focus lands anywhere, or after
 * `RESTORE_TIMEOUT_MS`. If focus is still lost then (the card never
 * mounted, as in a collapsed column), it gives up on the card through
 * `onGiveUp`, so a later change to `cells` doesn't pull focus back to it.
 */
export const useBoardFocusRestore = <TItem>({
    root,
    focusedKey,
    cells,
    windowedCellKeys,
    onGiveUp,
}: UseBoardFocusRestoreArgs<TItem>) => {
    // A value, so an equal list of keys doesn't cancel a pending restore.
    const windowedKey = windowedCellKeys.join(" ");
    useLayoutEffect(() => {
        if (root === null || focusedKey === null) {
            return undefined;
        }
        // Done once focus is on the card or elsewhere in the board; focus
        // outside it is in the card's popup (the key would be null otherwise).
        const refocus = () => {
            if (!isFocusLost(root)) {
                return root.contains(document.activeElement);
            }
            const card = findCard(root, focusedKey);
            card?.focus();
            return card !== null;
        };
        if (refocus()) {
            return undefined;
        }
        const observer = new MutationObserver(() => {
            if (refocus()) {
                stop();
            }
        });
        // Focus landing anywhere (the restored card included) ends the wait.
        const timeout = window.setTimeout(() => {
            stop();
            if (isFocusLost(root)) {
                onGiveUp();
            }
        }, RESTORE_TIMEOUT_MS);
        function stop() {
            observer.disconnect();
            window.clearTimeout(timeout);
            document.removeEventListener("focusin", stop, true);
        }
        document.addEventListener("focusin", stop, true);
        // The whole page, so a closing popup (a portal) counts too.
        observer.observe(document.body, { childList: true, subtree: true });
        return stop;
    }, [root, focusedKey, cells, windowedKey, onGiveUp]);
};
